import { DropDownButtonComponent } from "@syncfusion/ej2-react-splitbuttons";
import { LoadingButtonProps } from "./useLoadingButton";
import LoadingButton from "./LoadingButton";
import classNames from "classnames";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import { PrimaryButtonStyle } from "./PrimaryButton";
import { UCLColorKey, UCLColors } from "../colors/uclColors";
import { isNil } from "lodash";
import {
  Popover,
  PopoverInteractionKind,
  PopoverProps,
  Position,
} from "@blueprintjs/core";

export interface ActionButton<T>
  extends Omit<LoadingButtonProps, "disabled" | "onClick">,
    DefaultHtmlAttributes {
  actionKey?: string;
  visible?: (data?: T) => boolean;
  isDisabled?: (data?: T) => boolean;
  onActionClick?: (data?: T) => void;
  isPrimary?: boolean;
  color?: UCLColorKey;
  position?: "left" | "right";
  popover?: Omit<PopoverProps, "interactionKind">;
}

export interface ActionsButtonsProps<T> {
  actions: ActionButton<T>[];
  data?: T;
  maxButtonsDisplayed?: number;
  minimal?: boolean;
}

const ActionButtons = <T,>(props: ActionsButtonsProps<T>) => {
  const visibleActions = props.actions.filter((action) =>
    props.data && !isNil(action.visible) ? action.visible?.(props.data) : true
  );
  const diplayedActions = visibleActions.slice(0, props.maxButtonsDisplayed);
  const nonDisplayedActions =
    props.maxButtonsDisplayed && props.maxButtonsDisplayed > 0
      ? visibleActions.slice(props.maxButtonsDisplayed)
      : [];
  const dropdownButtonItems = nonDisplayedActions.map((action) => {
    return {
      ...action,
      content: action.text,
      disabled: action.isDisabled?.(props.data),
      onClick: () => action.onActionClick?.(props.data),
    };
  });

  const leftDisplayedActions =
    diplayedActions.filter((action) => action.position === "left") || [];
  const rightDisplayedActions =
    diplayedActions.filter((action) => action.position !== "left") || [];

  const leftDropdownButtonItems = dropdownButtonItems.filter(
    (action) => action.position === "left"
  );
  const rightDropdownButtonItems = dropdownButtonItems.filter(
    (action) => action.position !== "left"
  );

  const renderButton = (
    action: ActionButton<T>,
    resolvedColor: string | undefined
  ) => {
    const { actionKey, visible, isDisabled, onActionClick, ...rest } = action;
    const buttonProps = action.isPrimary
      ? {
          ...PrimaryButtonStyle,
          ...rest,
        }
      : rest;

    return (
      <LoadingButton
        {...buttonProps}
        minimal={isNil(props.minimal) ? action.minimal : props.minimal}
        disabled={action.isDisabled?.(props.data)}
        onClick={() => action.onActionClick?.(props.data)}
        style={
          resolvedColor
            ? {
                background: resolvedColor,
                borderColor: resolvedColor,
                color: "#fff",
              }
            : undefined
        }
      />
    );
  };

  const renderDisplayedActions = (actions: ActionButton<T>[]) => {
    return actions.map((action, idx) => {
      const resolvedColor = action.color ? UCLColors[action.color] : undefined;

      return (
        <div key={idx}>
          {action.popover ? (
            <Popover
              {...action.popover}
              portalClassName={
                action.popover.portalClassName || "ucl-action-buttons-popover"
              }
              interactionKind={PopoverInteractionKind.HOVER}
              position={action.popover.position || Position.TOP}
              usePortal={action.popover.usePortal || true}
            >
              {renderButton(action, resolvedColor)}
            </Popover>
          ) : (
            renderButton(action, resolvedColor)
          )}
        </div>
      );
    });
  };

  const renderDropdownButtonItems = (
    dropdownButtonItems: ActionButton<T>[]
  ) => {
    return (
      dropdownButtonItems.length > 0 && (
        <DropDownButtonComponent
          iconPosition="Top"
          iconCss="e-icons e-more-horizontal-1"
          cssClass={classNames("e-caret-hide", "ucl-action-buttons-dropdown", {
            "ucl-action-buttons-minimal": props.minimal,
          })}
          select={(e) => {
            e.item.onClick?.();
          }}
          items={dropdownButtonItems}
          itemTemplate={(action: ActionButton<T>) => {
            return (
              <div className="ucl-action-buttons-item">
                {action.popover ? (
                  <Popover {...action.popover}>
                    <LoadingButton
                      {...action}
                      minimal={true}
                      iconPosition="left"
                    />
                  </Popover>
                ) : (
                  <LoadingButton
                    {...action}
                    minimal={true}
                    iconPosition="left"
                  />
                )}
              </div>
            );
          }}
        />
      )
    );
  };

  return (
    <div className="ucl-action-buttons">
      <>
        {(leftDisplayedActions.length > 0 ||
          leftDropdownButtonItems.length > 0) && (
          <div className="ucl-action-buttons-left">
            {renderDisplayedActions(leftDisplayedActions)}
            {leftDropdownButtonItems.length > 0 &&
              renderDropdownButtonItems(leftDropdownButtonItems)}
          </div>
        )}
      </>
      <>
        {(rightDisplayedActions.length > 0 ||
          rightDropdownButtonItems.length > 0) && (
          <div className="ucl-action-buttons-right">
            {renderDisplayedActions(rightDisplayedActions)}
            {rightDropdownButtonItems.length > 0 &&
              renderDropdownButtonItems(rightDropdownButtonItems)}
          </div>
        )}
      </>
    </div>
  );
};
export default ActionButtons;
